import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  PlayIcon,
  XMarkIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ChartBarIcon,
  ServerIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import ApiClient from '../utils/ApiClient';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const IndexAdvisor = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indexCreationInProgress, setIndexCreationInProgress] = useState(false);
  const [currentlyRunningIndex, setCurrentlyRunningIndex] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, suggestion: null, action: null });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [stats, setStats] = useState({});
  const [refreshInterval, setRefreshInterval] = useState(null);

  const statusTabs = ['ALL', 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'IGNORED'];

  useEffect(() => {
    fetchSuggestions();
    fetchStats();
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [selectedTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (indexCreationInProgress && !refreshInterval) {
      const interval = setInterval(() => {
        fetchSuggestions();
      }, 2000);
      setRefreshInterval(interval);
    } else if (!indexCreationInProgress && refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [indexCreationInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSuggestions = async () => {
    try {
      const response = await ApiClient.get('/api/index-advisor/suggestions', {
        params: { status: statusTabs[selectedTab] }
      });
      
      setSuggestions(response.data.suggestions || []);
      setIndexCreationInProgress(response.data.indexCreationInProgress || false);
      setCurrentlyRunningIndex(response.data.currentlyRunningIndex || null);
      setError(null);
    } catch (err) {
      setError('Failed to fetch index suggestions');
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await ApiClient.get('/api/index-advisor/status');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleCreateIndex = async (suggestion) => {
    setConfirmDialog({ open: false });
    
    try {
      await ApiClient.post(
        `/api/index-advisor/suggestions/${suggestion.id}/create-index`
      );
      
      showNotification('Index creation started successfully', 'success');
      fetchSuggestions();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to create index', 'error');
    }
  };

  const handleIgnoreSuggestion = async (suggestion) => {
    setConfirmDialog({ open: false });
    
    try {
      await ApiClient.post(
        `/api/index-advisor/suggestions/${suggestion.id}/ignore`
      );
      
      showNotification('Suggestion marked as ignored', 'success');
      fetchSuggestions();
    } catch (err) {
      showNotification('Failed to ignore suggestion', 'error');
    }
  };

  const handleTriggerAnalysis = async () => {
    try {
      await ApiClient.post('/api/index-advisor/analyze');
      
      showNotification('Analysis triggered successfully', 'success');
      setTimeout(fetchSuggestions, 5000);
    } catch (err) {
      showNotification('Failed to trigger analysis', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'info' }), 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'IGNORED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatExecutionTime = (ms) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6 sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Index Advisor</h1>
            <p className="mt-2 text-sm text-gray-700">
              Monitor database performance and manage index suggestions
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
            <button
              onClick={fetchSuggestions}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={handleTriggerAnalysis}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Analyze Queries
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.pendingCount || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ServerIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.completedCount || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XMarkIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.failedCount || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XMarkIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Ignored</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.ignoredCount || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Alert */}
        {indexCreationInProgress && currentlyRunningIndex && (
          <div className="mb-6 rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <InformationCircleIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  Index creation in progress: {currentlyRunningIndex.suggestedIndexName}
                </h3>
                {currentlyRunningIndex.progress && (
                  <div className="mt-2">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${currentlyRunningIndex.progress.progressPercent || 0}%` }}
                      ></div>
                    </div>
                    <p className="mt-1 text-xs text-blue-700">
                      {currentlyRunningIndex.progress.currentStatus}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XMarkIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white shadow rounded-lg">
          {/* Tabs */}
          <div className="px-4 py-5 sm:p-6">
            <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
              <TabList className="flex space-x-1 rounded-xl bg-gray-100 p-1">
                {statusTabs.map((status) => (
                  <Tab
                    key={status}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white text-blue-700 shadow'
                          : 'text-gray-700 hover:bg-white/[0.12] hover:text-gray-900'
                      )
                    }
                  >
                    {status}
                  </Tab>
                ))}
              </TabList>

              <TabPanels className="mt-6">
                {statusTabs.map((status, idx) => (
                  <TabPanel key={idx}>
                    {/* Table */}
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Table / Suggested Index
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Columns
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Avg Time
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Executions / Improvement
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {loading ? (
                            <tr>
                              <td colSpan="6" className="px-6 py-4 text-center">
                                <div className="flex justify-center">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                </div>
                              </td>
                            </tr>
                          ) : suggestions.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                No suggestions found
                              </td>
                            </tr>
                          ) : (
                            suggestions.map((suggestion) => (
                              <tr key={suggestion.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <div className="text-gray-900">{suggestion.tableSchema}.{suggestion.tableName}</div>
                                  <div className="text-sm font-mono text-gray-600">{suggestion.suggestedIndexName}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                  {suggestion.suggestedIndexColumns}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                  {formatExecutionTime(suggestion.avgExecutionTimeMs)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <div className="text-sm text-gray-900">{formatNumber(suggestion.executionCount)}</div>
                                  <div className="mt-1">
                                    <span className={classNames(
                                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                                      suggestion.potentialImprovementPercent > 70 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                    )}>
                                      {suggestion.potentialImprovementPercent}%
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={classNames(
                                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                                    getStatusColor(suggestion.status)
                                  )}>
                                    {suggestion.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                                  <button
                                    onClick={() => setSelectedSuggestion(suggestion)}
                                    className="text-indigo-600 hover:text-indigo-900 text-sm"
                                    title="View Details"
                                  >
                                    <InformationCircleIcon className="h-4 w-4" />
                                  </button>
                                  {suggestion.status === 'PENDING' && (
                                    <>
                                      <button
                                        onClick={() => setConfirmDialog({ 
                                          open: true, 
                                          suggestion, 
                                          action: 'create' 
                                        })}
                                        disabled={indexCreationInProgress}
                                        className="text-green-600 hover:text-green-900 disabled:text-gray-400 text-sm"
                                        title="Create Index"
                                      >
                                        <PlayIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => setConfirmDialog({ 
                                          open: true, 
                                          suggestion, 
                                          action: 'ignore' 
                                        })}
                                        className="text-yellow-600 hover:text-yellow-900 text-sm"
                                        title="Ignore"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>
          </div>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })} className="relative z-50">
          <div className="fixed inset-0 bg-black/25" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-md rounded bg-white p-6">
              <DialogTitle className="text-lg font-medium text-gray-900 mb-4">
                {confirmDialog.action === 'create' ? 'Create Index' : 'Ignore Suggestion'}
              </DialogTitle>
              <p className="text-sm text-gray-500 mb-4">
                {confirmDialog.action === 'create' 
                  ? `Are you sure you want to create the index "${confirmDialog.suggestion?.suggestedIndexName}"?`
                  : `Are you sure you want to ignore this suggestion?`}
              </p>
              {confirmDialog.action === 'create' && confirmDialog.suggestion && (
                <div className="mb-4 p-3 bg-gray-100 rounded text-sm font-mono">
                  CREATE INDEX {confirmDialog.suggestion.suggestedIndexName} ON {confirmDialog.suggestion.tableSchema}.{confirmDialog.suggestion.tableName} ({confirmDialog.suggestion.suggestedIndexColumns})
                </div>
              )}
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setConfirmDialog({ open: false })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmDialog.action === 'create') {
                      handleCreateIndex(confirmDialog.suggestion);
                    } else {
                      handleIgnoreSuggestion(confirmDialog.suggestion);
                    }
                  }}
                  className={classNames(
                    'px-4 py-2 text-sm font-medium text-white rounded-md',
                    confirmDialog.action === 'create' 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  )}
                >
                  Confirm
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Details Dialog */}
        <Dialog open={Boolean(selectedSuggestion)} onClose={() => setSelectedSuggestion(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/25" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-2xl rounded bg-white p-6">
              <DialogTitle className="text-lg font-medium text-gray-900 mb-4">
                Index Suggestion Details
              </DialogTitle>
              {selectedSuggestion && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Table</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedSuggestion.tableSchema}.{selectedSuggestion.tableName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={classNames(
                        'mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                        getStatusColor(selectedSuggestion.status)
                      )}>
                        {selectedSuggestion.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Query Pattern</label>
                    <div className="mt-1 p-3 bg-gray-100 rounded text-sm font-mono">
                      {selectedSuggestion.queryPattern || 'No query pattern available'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Create Index Statement</label>
                    <div className="mt-1 p-3 bg-gray-100 rounded text-sm font-mono">
                      CREATE INDEX {selectedSuggestion.suggestedIndexName} ON {selectedSuggestion.tableSchema}.{selectedSuggestion.tableName} ({selectedSuggestion.suggestedIndexColumns})
                    </div>
                  </div>
                  {selectedSuggestion.errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <XMarkIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{selectedSuggestion.errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedSuggestion(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Notification */}
        {notification.show && (
          <div className="fixed bottom-4 right-4 z-50">
            <div className={classNames(
              'rounded-md p-4 shadow-lg',
              notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
            )}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {notification.type === 'success' ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <XMarkIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={classNames(
                    'text-sm',
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  )}>
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndexAdvisor;